import { create } from "zustand";
import { persist } from "zustand/middleware";

type AppState = {
  activeTab: "cities" | "itinerary" | "hotels";
  activeSortByMatch: boolean;
  hotelPrompt: string;
  hotelPromptIdentifier: number | null;
  appHeight: number | null;
  isApp: boolean;
  appWidth: number;
  isSmall: boolean;
  referralCode: string | null;
  hotelsMatchRates: { [key: number]: number };
  hasAiMatchLoaded: boolean;
  popUpStatus: { [key: string]: boolean };
  showAiHotelPrompt: boolean;
  cityCenter: { lat: number; lng: number } | null;
  setCiyCenter: (ciyCenter: { lat: number; lng: number } | null) => void;
  setAppWidth: (appWidth: number) => void;
  setIsSmall: (isSmall: boolean) => void;
  setShowAiHotelPrompt: (showAiHotelPrompt: boolean) => void;
  setPopUpStatus: (popUpStatus: { [key: string]: boolean }) => void;
  togglePopUp: (key: string) => void;
  setHasAiMatchLoaded: (status: boolean) => void;
  addHotelsMatchRates: (code: number, rate: number) => void;
  setHotelPromptIdentifier: (hotelPromptIdentifier: number | null) => void;
  setAppHeight: (height: number) => void;
  resrtHotelsMatchRates: () => void;
  setActiveSortByMatch: (activeSortByMatch: boolean) => void;
  setHotelPrompt: (hotelPrompt: string) => void;
  setReferralCode: (referralCode: string | null) => void;
  setIsApp: (isApp: boolean) => void;
  setActiveTab: (tab: "cities" | "itinerary" | "hotels") => void;
};

// Use persist middleware to store only hotelPrompt in localStorage
const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      referralCode: null,
      activeTab: "hotels", // default value
      hotelPrompt: "", // this will be stored in localStorage
      hotelsMatchRates: [],
      cityCenter: null,
      setCiyCenter: (cityCenter: { lat: number; lng: number } | null) =>
        set({ cityCenter }),
      activeSortByMatch: false,
      appHeight: null,
      appWidth: 0,
      isSmall: false,
      isApp: false,
      hasAiMatchLoaded: false,
      popUpStatus: {},
      showAiHotelPrompt: false,
      setIsSmall: (isSmall: boolean) => set({ isSmall }),
      setAppWidth: (appWidth: number) => set({ appWidth }),
      setShowAiHotelPrompt: (showAiHotelPrompt: boolean) =>
        set({ showAiHotelPrompt }),
      setPopUpStatus: (popUpStatus: { [key: string]: boolean }) =>
        set((state) => {
          return { popUpStatus: { ...state.popUpStatus, ...popUpStatus } };
        }),
      togglePopUp: (key: string) =>
        set((state) => {
          state.popUpStatus[key] = !state.popUpStatus[key];
          return { popUpStatus: state.popUpStatus };
        }),
      setHasAiMatchLoaded(status) {
        set({ hasAiMatchLoaded: status });
      },
      hotelPromptIdentifier: null,
      setHotelPromptIdentifier: (hotelPromptIdentifier) =>
        set({ hotelPromptIdentifier }),
      setAppHeight: (appHeight: number) => set({ appHeight }),
      setIsApp: (isApp: boolean) => set({ isApp }),
      setActiveSortByMatch: (activeSortByMatch) => set({ activeSortByMatch }),

      addHotelsMatchRates: (newHotelRateCode, newHotelRateMatch) =>
        set((state) => {
          const exists = state.hotelsMatchRates[newHotelRateCode];

          if (!exists) {
            state.hotelsMatchRates[newHotelRateCode] = newHotelRateMatch;
            return {
              hotelsMatchRates: state.hotelsMatchRates,
            };
          }

          return state;
        }),
      resrtHotelsMatchRates: () =>
        set((state) => ({
          hotelsMatchRates: [],
        })),

      setHotelPrompt: (hotelPrompt: string) => set({ hotelPrompt }),
      setReferralCode: (referralCode: string | null) => set({ referralCode }),

      setActiveTab: (tab: "cities" | "itinerary" | "hotels") =>
        set({ activeTab: tab }),
    }),
    {
      name: "app-storage", // localStorage key
      version: 2,
      partialize: (state) => ({
        hotelPrompt: state.hotelPrompt,
        referralCode: state.referralCode,
      }),
    },
  ),
);

export default useAppStore;
